import type { FC, KeyboardEventHandler } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { Checkbox } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import type { FormatNumberOptions } from 'react-intl';
import { Field } from '../../../../../components/field/Field';
import { ListReveal } from '../../../../../components/list-reveal/ListReveal';
import { DEFAULT_CURRENCY } from '../../../../../constants';
import { Text } from '../../../../../ui-components';
import { regex } from '../../../../../utils/validation';
import { FilterBehavior } from '../../../hooks/use-search/use-search-types';
import { type PriceRangeFormValues, usePriceRangeFields } from './usePriceRangeFields';
import type { UseSearchResult } from '../../../hooks/use-search/use-search-types';

const formatPriceRange = (minPrice: string, maxPrice: string): string | undefined => {
    if (!minPrice && !maxPrice) {
        return undefined;
    }

    const formattedMin = minPrice || '*';
    const formattedMax = maxPrice || '*';

    return `${formattedMin}-${formattedMax}`;
};

export type PriceFilterSectionProps = {
    search: UseSearchResult;
};

export const PriceFilterSection: FC<PriceFilterSectionProps> = ({
    search: {
        searchParams,
        filters,
        isFilterSelected,
        onChangeRangeFilter,
        onRemoveFilter,
        isCustomPrice,
    },
}) => {
    const intl = useIntl();

    const getCustomPriceValue = useCallback((): string => {
        const predefinedRanges =
            filters?.byRange?.price?.map((range) => `${range.start}-${range.end}`) || [];
        const customPriceValue = searchParams.price?.find(
            (price) => !predefinedRanges.includes(price),
        );

        return customPriceValue || '';
    }, [filters?.byRange?.price, searchParams.price]);

    const getDefaultCustomPriceValues = useCallback((): { minPrice: string; maxPrice: string } => {
        if (!isCustomPrice || !searchParams.price?.length) {
            return { minPrice: '', maxPrice: '' };
        }

        const customPriceValue = getCustomPriceValue();
        if (!customPriceValue) {
            return { minPrice: '', maxPrice: '' };
        }

        const [minPriceStr, maxPriceStr] = customPriceValue.split('-');
        const minPrice = minPriceStr === '*' || minPriceStr === '0' ? '' : minPriceStr;
        const maxPrice = maxPriceStr === '*' ? '' : maxPriceStr;

        return {
            minPrice: Number.isNaN(Number(minPrice)) ? '' : minPrice,
            maxPrice: Number.isNaN(Number(maxPrice)) ? '' : maxPrice,
        };
    }, [isCustomPrice, searchParams.price, getCustomPriceValue]);

    const { minPrice, maxPrice } = getDefaultCustomPriceValues();

    const methods = useForm<PriceRangeFormValues>({
        mode: 'onChange',
        defaultValues: {
            minPrice,
            maxPrice,
        },
    });

    const { trigger, getValues, reset } = methods;
    const customPriceRangeFields = usePriceRangeFields(methods);

    const onRegularFilterChange = (value: string): void => {
        const customPriceValue = getCustomPriceValue();
        // if custom price is selected, replace the custom price with the regular filter
        // otherwise, append the regular filter
        // we cannot use onRemoveFilter because both onRemoveFilter() and onChangeRangeFilter() read
        // from location.search when they start, but location.search doesn't update immediately after
        // the first function updates the URL.
        onChangeRangeFilter(
            'price',
            value,
            customPriceValue ? FilterBehavior.REPLACE : FilterBehavior.APPEND,
        );
    };

    const handleOnChangeCustomFilter = (): void => {
        if (isCustomPrice) {
            onRemoveFilter('price', getCustomPriceValue());
        } else {
            const { minPrice: minPriceValue, maxPrice: maxPriceValue } = getValues();

            // If range is invalid or empty, set minPrice to 0
            const minPriceValueProcessed = minPriceValue || '0';
            const maxPriceValueProcessed = maxPriceValue || '';

            const range = formatPriceRange(minPriceValueProcessed, maxPriceValueProcessed);
            if (range) {
                onChangeRangeFilter('price', range, FilterBehavior.REPLACE);
            }
        }
    };

    const onCustomPriceChange = async (): Promise<void> => {
        const isValid = await trigger(['minPrice', 'maxPrice']);

        if (!isValid) return;

        if (isCustomPrice) {
            const customPriceValue = getCustomPriceValue();
            if (customPriceValue) {
                const { minPrice: minPriceValue, maxPrice: maxPriceValue } = getValues();
                const range = formatPriceRange(minPriceValue, maxPriceValue);

                if (range && range !== customPriceValue) {
                    onChangeRangeFilter('price', range, FilterBehavior.REPLACE);
                }
            }
        } else {
            // If custom not selected yet, select it and apply range on blur
            handleOnChangeCustomFilter();
        }
    };

    const onCustomPriceEnter: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') {
            onCustomPriceChange().catch(console.error);
        }
    };

    const processPriceInput = (evt: React.ChangeEvent<HTMLInputElement>): string =>
        evt.target.value.replace(regex.onlyNumbers, '');

    const currencyOptions = useMemo(
        () =>
            ({
                style: 'currency',
                currency: DEFAULT_CURRENCY,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }) as FormatNumberOptions,
        [],
    );

    // Reset form only when component mounts or URL changes (not on every form interaction)
    useEffect(() => {
        const { minPrice: newMinPrice, maxPrice: newMaxPrice } = getDefaultCustomPriceValues();
        reset({
            minPrice: newMinPrice,
            maxPrice: newMaxPrice,
        });
    }, [searchParams.price, reset, getDefaultCustomPriceValues]);

    return (
        <div className="ml-[2px] flex flex-col gap-y-4">
            <ListReveal
                data={filters?.byRange?.price ?? []}
                keyExtractor={(range) => `${range.start}-${range.end}`}
                listContainerClassName="grid grid-cols-2 gap-y-4 gap-x-2"
                initialNumToRender={(filters?.byRange?.price ?? []).length}
                renderItem={({ item: range }) => {
                    const value = `${range.start}-${range.end}`;
                    let label = null;
                    if (range.start === '*' && typeof range.end === 'number') {
                        label = `${intl.formatMessage({ id: 'search.filters.under', defaultMessage: 'Under' })} ${intl.formatNumber(range.end, currencyOptions)}`;
                    } else if (typeof range.start === 'number' && typeof range.end === 'number') {
                        label = `${intl.formatNumber(range.start, currencyOptions)} - ${intl.formatNumber(range.end, currencyOptions)}`;
                    } else if (typeof range.start === 'number' && range.end === '*') {
                        label = `${intl.formatNumber(range.start, currencyOptions)} - ${intl.formatMessage({ id: 'search.filters.above', defaultMessage: '& Above' })}`;
                    }

                    return (
                        <Checkbox
                            variant="new-style"
                            key={value}
                            isChecked={isFilterSelected('price', value)}
                            onChange={() => onRegularFilterChange(value)}
                            data-testid={`price-checkbox-${value}`}
                            isDisabled={range.count === 0}
                        >
                            <Text>{`${label} (${range.count})`}</Text>
                        </Checkbox>
                    );
                }}
            />
            <div className="col-span-2 flex">
                <Checkbox
                    id="custom-price-checkbox"
                    variant="new-style"
                    name="custom"
                    isChecked={isCustomPrice}
                    onChange={handleOnChangeCustomFilter}
                    data-testid="price-checkbox-custom"
                />
                <Field
                    {...customPriceRangeFields.minPrice}
                    className="ml-2"
                    inputProps={({ onChange }: { onChange: (value: string) => void }) => ({
                        inputMode: 'numeric',
                        placeholder: '$ 0',
                        onBlur: onCustomPriceChange,
                        onKeyDown: onCustomPriceEnter,
                        onChange: (evt: React.ChangeEvent<HTMLInputElement>): void => {
                            onChange(processPriceInput(evt));
                        },
                    })}
                />
                <Field
                    {...customPriceRangeFields.maxPrice}
                    className="ml-4"
                    inputProps={({ onChange }: { onChange: (value: string) => void }) => ({
                        inputMode: 'numeric',
                        placeholder: '$ 0',
                        onBlur: onCustomPriceChange,
                        onKeyDown: onCustomPriceEnter,
                        onChange: (evt: React.ChangeEvent<HTMLInputElement>): void => {
                            onChange(processPriceInput(evt));
                        },
                    })}
                />
            </div>
        </div>
    );
};
